import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { GitBranch, Eye, Settings, BadgeCheck, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Κοστολόγηση Στράντζας (Κάμψης) για Λαμαρίνα',
      subtitle: 'Αυτόματη ανίχνευση γραμμών κάμψης από τα αρχεία CAD και υπολογισμός κόστους κάμψης ανά εξάρτημα — χωρίς χειροκίνητο μέτρημα.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε τις Λειτουργίες',
    },
    intro: {
      title: 'Πώς λειτουργεί η κοστολόγηση κάμψης',
      body: 'Όταν εισάγετε ένα αρχείο DXF ή STEP, το Workshop Assistant ανιχνεύει αυτόματα τις γραμμές κάμψης στη γεωμετρία και τις μετρά. Ο Επεξεργαστής Γεωμετρίας DXF σάς επιτρέπει επίσης να ταξινομήσετε χειροκίνητα γραμμές κάμψης όπου χρειάζεται, ενώ το κόστος κάμψης υπολογίζεται αυτόματα βάσει του αριθμού κάμψεων και των ρυθμίσεων μηχανήματος κάμψης που έχετε ορίσει.',
    },
    features: {
      title: 'Λειτουργίες κοστολόγησης κάμψης',
      items: [
        { icon: 'branch', title: 'Αυτόματη Ανίχνευση Κάμψεων', description: 'Ανιχνεύει γραμμές κάμψης απευθείας από τη γεωμετρία DXF/STEP, χωρίς χειροκίνητη καταμέτρηση.' },
        { icon: 'eye', title: 'Οπτική Επαλήθευση', description: 'Δείτε και επεξεργαστείτε τις ανιχνευμένες γραμμές κάμψης απευθείας πάνω στη γεωμετρία πριν την κοστολόγηση.' },
        { icon: 'settings', title: 'Ρυθμίσεις Μηχανήματος Κάμψης', description: 'Ορίστε κόστος ανά κάμψη, ταχύτητα και εργατικά για το μηχάνημα κάμψης του εργαστηρίου σας.' },
        { icon: 'badge', title: 'Ετικέτα Ταξινόμησης "Bent"', description: 'Κάθε εξάρτημα με κάμψεις επισημαίνεται αυτόματα ώστε να ξεχωρίζει εύκολα στη λίστα προσφορών.' },
      ],
    },
    faq: [
      { q: 'Πώς υπολογίζεται το κόστος κάμψης;', a: 'Βάσει του αριθμού ανιχνευμένων γραμμών κάμψης ανά εξάρτημα, πολλαπλασιαζόμενο με το κόστος ανά κάμψη που ορίζετε στον Επεξεργαστή Παραμέτρων.' },
      { q: 'Ανιχνεύει αυτόματα τις κάμψεις από DXF και STEP;', a: 'Ναι, τόσο τα αρχεία DXF όσο και STEP αναλύονται αυτόματα για γραμμές κάμψης, με δυνατότητα χειροκίνητης διόρθωσης στον Επεξεργαστή Γεωμετρίας.' },
      { q: 'Μπορώ να δω πόσες κάμψεις έχει ένα εξάρτημα πριν το κοστολογήσω;', a: 'Ναι, ο πίνακας ανιχνευμένων μετρήσεων δείχνει τον αριθμό κάμψεων ανά εξάρτημα πριν δημιουργήσετε την προσφορά.' },
    ],
    cta: { title: 'Δοκιμάστε την Κοστολόγηση Κάμψης', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και δείτε πόσο γρήγορα κοστολογείτε εξαρτήματα με κάμψεις.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Bend Costing for Sheet Metal Parts',
      subtitle: 'Automatic bend-line detection from your CAD files, with bend cost calculated per part — no manual counting.',
      cta1: 'Download Free Trial',
      cta2: 'Explore Features',
    },
    intro: {
      title: 'How bend costing works',
      body: 'When you import a DXF or STEP file, Workshop Assistant automatically detects bend lines in the geometry and measures them. The DXF Geometry Editor also lets you manually classify bend lines where needed, and bend cost is calculated automatically based on the bend count and the bending machine settings you\'ve configured.',
    },
    features: {
      title: 'Bend costing features',
      items: [
        { icon: 'branch', title: 'Automatic Bend Detection', description: 'Detects bend lines directly from DXF/STEP geometry — no manual counting required.' },
        { icon: 'eye', title: 'Visual Verification', description: 'View and edit detected bend lines directly on the geometry before costing.' },
        { icon: 'settings', title: 'Bending Machine Settings', description: 'Set cost per bend, speed, and labor rate for your workshop\'s bending machine.' },
        { icon: 'badge', title: '"Bent" Classification Badge', description: 'Every part with bends is automatically flagged so it stands out in the offers list.' },
      ],
    },
    faq: [
      { q: 'How is bend cost calculated?', a: 'Based on the number of detected bend lines per part, multiplied by the cost-per-bend you set in the Parameters Editor.' },
      { q: 'Does it auto-detect bends from both DXF and STEP?', a: 'Yes — both DXF and STEP files are automatically analyzed for bend lines, with manual correction available in the Geometry Editor.' },
      { q: 'Can I see how many bends a part has before costing it?', a: 'Yes, the detected-metrics panel shows the bend count per part before you generate the offer.' },
    ],
    cta: { title: 'Try Bend Costing Yourself', subtitle: 'Download the free trial and see how fast you can cost parts with bends.', btn: 'Download Free Trial' },
  },
}

const icons = { branch: <GitBranch className="h-10 w-10" />, eye: <Eye className="h-10 w-10" />, settings: <Settings className="h-10 w-10" />, badge: <BadgeCheck className="h-10 w-10" /> }

const BendCosting = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Bend Costing Software — Automatic Bend Detection & Pricing"
        description="Automatically detect bend lines from DXF/STEP files and calculate bend cost per part with Workshop Assistant's bend costing tools."
        path="/bend-costing"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/features" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="lg:order-2">
            <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
            <p className="text-lg text-gray-600">{t.intro.body}</p>
          </div>
          <div className="lg:order-1 rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/dxf-editor.png?v=2`} alt={t.hero.title} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default BendCosting
